import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HeroSection from '../components/HeroSection';
import ShelfRow from '../components/ShelfRow';
import { Toaster } from 'sonner';

import { BACKEND_URL, API } from '../utils/api';
const R2_URL = process.env.REACT_APP_R2_URL || BACKEND_URL + "/api/static";

const HomePage = () => {
  const [allBooks, setAllBooks] = useState([]);
  const [shelves, setShelves] = useState({ desktop: [], mobile: [] });
  const navigate = useNavigate();

  // Fetch books from API
  useEffect(() => {
    fetch(`${API}/books`)
      .then((r) => r.json())
      .then((apiBooks) => {
        if (apiBooks && apiBooks.length > 0) {
          const booksWithCovers = apiBooks.map((b) => ({
            ...b,
            id: b.book_id || b.slug,
            coverUrl: `${R2_URL}/books/${b.imageFolder}/cover.webp`,
            imagePages: b.pageCount,
          }));
          setAllBooks(booksWithCovers);
          const desktop = [], mobile = [];
          for (let i = 0; i < booksWithCovers.length; i += 8) desktop.push({ id: `d${i}`, books: booksWithCovers.slice(i, i + 8) });
          for (let i = 0; i < booksWithCovers.length; i += 3) mobile.push({ id: `m${i}`, books: booksWithCovers.slice(i, i + 3) });
          setShelves({ desktop, mobile });
          setSearchText(`Buscar ${booksWithCovers.length} libros`);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-dvh bg-background font-sans">
      <Helmet>
        <title>Libritos Cristianos Ilustrados para Niños | Museo de la Fe</title>
        <meta name="description" content="Libritos cristianos ilustrados para niños de 3 a 10 años que enseñan valores bíblicos con amor. Formato digital interactivo. Disponible en Latinoamérica y Estados Unidos." />
        <meta property="og:title" content="Museo de la Fe — Libritos Cristianos para Niños" />
        <meta property="og:description" content="Historias bíblicas ilustradas para los más pequeños. Formato flipbook interactivo." />
        <meta property="og:url" content="https://museodelafe.com" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="es_ES" />
        {allBooks.length > 0 && <meta property="og:image" content={`${R2_URL}/books/${allBooks[0].imageFolder}/cover.webp`} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Museo de la Fe — Libritos Cristianos para Niños" />
        <meta name="twitter:description" content="Historias bíblicas ilustradas para los más pequeños. Formato flipbook interactivo." />
        {allBooks.length > 0 && <meta name="twitter:image" content={`${R2_URL}/books/${allBooks[0].imageFolder}/cover.webp`} />}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://museodelafe.com" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Museo de la Fe",
          "url": "https://museodelafe.com",
          "description": "Tienda de libritos cristianos ilustrados para niños hispanohablantes de 3 a 10 años",
          "inLanguage": "es",
          "potentialAction": { "@type": "SearchAction", "target": "https://museodelafe.com/?q={search_term_string}", "query-input": "required name=search_term_string" }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Museo de la Fe",
          "url": "https://museodelafe.com",
          "description": "Tienda de libritos cristianos ilustrados para niños hispanohablantes de 3 a 10 años",
          "inLanguage": "es",
          "contactPoint": { "@type": "ContactPoint", "email": "admin@museodelafe.com", "contactType": "customer service" }
        })}</script>
        {allBooks.length > 0 && <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Libritos Cristianos para Niños — Museo de la Fe",
          "itemListElement": allBooks.map((b, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": b.title,
            "url": `https://museodelafe.com/libro/${b.slug}`
          }))
        })}</script>}
      </Helmet>
      <Toaster position="top-center" />
      <Header />

      <main>
        <HeroSection books={allBooks} />

        {/* Book Shelves — Desktop */}
        <section className="hidden md:block mx-auto max-w-7xl px-4 md:px-6 pb-20 select-none">
          {shelves.desktop.map((shelf, i) => (
            <ShelfRow key={shelf.id} icons={shelf.books} index={i} />
          ))}
        </section>

        {/* Book Shelves — Mobile (3 per row) */}
        <section className="md:hidden mx-auto px-2 pb-16 select-none">
          {shelves.mobile.map((shelf, i) => (
            <ShelfRow key={shelf.id} icons={shelf.books} index={i} />
          ))}
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
